import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  getDeletePermissions,
  getMe,
  putDeletePermissions,
  type DeletePermissionsInfo,
  type MeInfo,
} from '../lib/settings';
import { drain, retryFailedItems } from '../lib/syncQueue';

export function Settings() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeInfo | null>(null);
  const [perms, setPerms] = useState<DeletePermissionsInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [savingPerm, setSavingPerm] = useState(false);
  const [failedCount, setFailedCount] = useState(0);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, p] = await Promise.all([
          getMe(),
          getDeletePermissions().catch(
            () =>
              ({
                allow_inspector_delete: false,
                updated_at: null,
              }) as DeletePermissionsInfo,
          ),
        ]);
        if (cancelled) return;
        setMe(m);
        setPerms(p);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Global sync status: count all failed items across jobs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Use an empty-string job filter to match nothing, then fetch failed for each known job.
      // For MVP simplicity: skip per-job aggregation; show only after a retry.
      if (cancelled) return;
      setFailedCount(0);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function togglePerm(next: boolean) {
    if (!me?.is_admin || savingPerm) return;
    setSavingPerm(true);
    setErr(null);
    try {
      const updated = await putDeletePermissions(next);
      setPerms(updated);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSavingPerm(false);
    }
  }

  async function retryAllFailed() {
    if (retrying) return;
    setRetrying(true);
    try {
      await retryFailedItems();
      void drain();
      // Give drain a beat to pick things up; for MVP we don't track global pending counts here.
    } finally {
      setRetrying(false);
    }
  }

  async function signOut() {
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore — we'll still clear local state
    }
    navigate('/');
  }

  if (err && !me) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <Link to="/" className="text-sm text-gray-600 underline">
          ‹ Jobs
        </Link>
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      </div>
    );
  }
  if (!me) return <div className="p-4 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link to="/" className="text-sm text-gray-600 underline">
        ‹ Jobs
      </Link>
      <h1 className="mt-2 text-xl font-bold">Settings</h1>

      {err && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Account */}
      <Section title="Account">
        <Row label="Name" value={me.name ?? '—'} />
        <Row label="Email" value={me.email ?? '—'} />
        <Row label="Organization" value={me.org_name ?? '—'} />
        <Row label="Role" value={me.role || 'member'} />
      </Section>

      {/* Permissions — read-only view */}
      <Section title="Permissions">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Inspector photo delete</div>
            <div className="text-xs text-gray-500">
              {perms?.allow_inspector_delete
                ? 'Enabled for this org.'
                : 'Disabled. Contact an admin to change.'}
            </div>
          </div>
          {me.is_admin ? (
            <button
              onClick={() => void togglePerm(!perms?.allow_inspector_delete)}
              disabled={savingPerm}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                perms?.allow_inspector_delete
                  ? 'bg-eva-teal text-white'
                  : 'bg-gray-200 text-gray-700'
              } disabled:opacity-50`}
            >
              {perms?.allow_inspector_delete ? 'ON' : 'OFF'}
            </button>
          ) : (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                perms?.allow_inspector_delete
                  ? 'bg-eva-teal text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {perms?.allow_inspector_delete ? 'ON' : 'OFF'}
            </span>
          )}
        </div>
      </Section>

      {/* Sync */}
      <Section title="Sync">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            {failedCount > 0
              ? `${failedCount} item${failedCount === 1 ? '' : 's'} failed across jobs`
              : 'No known failures.'}
          </div>
          <button
            onClick={() => void retryAllFailed()}
            disabled={retrying}
            className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-semibold disabled:opacity-50"
          >
            {retrying ? 'Retrying…' : 'Retry failed'}
          </button>
        </div>
        <div className="mt-1 text-[11px] text-gray-500">
          Per-job sync status is also visible on each card on the Home screen.
        </div>
      </Section>

      {/* Admin-only hint for where else to configure */}
      {me.is_admin && (
        <Section title="Admin">
          <div className="text-sm text-gray-700">
            Photo compression settings and full taxonomy management live in EVA web.
            Changes there reflect here automatically.
          </div>
        </Section>
      )}

      <button
        onClick={() => void signOut()}
        className="mt-8 w-full rounded-lg border border-red-300 bg-white py-3 text-sm font-semibold text-red-600"
      >
        Sign out
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5 rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-gray-500">
        {title}
      </div>
      <div className="space-y-1 text-sm">{children}</div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-gray-500">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
